import { useDatos } from "../api";
import { Carga } from "../componentes/Carga";
import { TextoMensaje } from "../componentes/TextoMensaje";
import { Alert, Badge, Button, Card, Eyebrow, Heading, KeyValue, KeyValueList, Page, Text, TopBar, useToast } from "../ds";
import { ESTADOS_APERTURA } from "../formato";
import type { Aperturas, Plantillas as DatosPlantillas } from "../tipos";
import s from "./Plantillas.module.css";

export function Plantillas() {
  const { datos: p, error, recargar } = useDatos<DatosPlantillas>("/api/plantillas");
  const { datos: aperturas } = useDatos<Aperturas>("/api/aperturas");
  const { mostrar } = useToast();

  const copiar = async (texto: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      mostrar({ title: "Texto copiado", tone: "success" });
    } catch {
      mostrar({ title: "No se pudo copiar", message: "Seleccioná el texto y copialo a mano.", tone: "warning" });
    }
  };

  return (
    <>
      <TopBar title="Plantillas" subtitle={p ? `Los textos que salen, con ${p.ejemplo.nombre} (${p.ejemplo.zona}) de ejemplo.` : "Los textos que salen."} />
      <Page width="content">
        {!p ? (
          <Carga error={error} onReintentar={recargar} filas={10} />
        ) : (
          <>
            <Alert tone="info" title="Los textos viven en el código">
              Se editan en <code>src/outreach/plantillas.ts</code>. Cambiarlos acá no está disponible a propósito: cada cambio pasa por revisión antes de llegarle a una inmobiliaria.
            </Alert>

            <Card padding="lg">
              <div className={s.cabecera}>
                <div>
                  <Eyebrow>WhatsApp · {p.whatsapp.secuencia}</Eyebrow>
                  <Heading level={2} size="md">
                    Primer mensaje
                  </Heading>
                </div>
                <Badge tone={p.whatsapp.modo === "plantilla" ? "brand" : "warning"} size="md" dot>
                  {p.whatsapp.modo === "plantilla" ? "Plantilla de Meta" : "Texto libre"}
                </Badge>
              </div>
              {p.whatsapp.modo === "plantilla" ? (
                <>
                  <Text size="sm">
                    Las aperturas que se pueden elegir en la cola. Sólo estas: una plantilla de seguimiento aprobada en Meta no aparece, así no sale como primer
                    mensaje por error. Para probar una nueva, se agrega a <code>APERTURAS_WHATSAPP</code> y se carga en WhatsApp Manager con el mismo nombre; el
                    tablero compara cómo rinde cada una.
                  </Text>
                  <div className={s.pasos}>
                    {p.whatsapp.aperturas.map((a) => {
                      const estado = aperturas?.lista.find((x) => x.nombre === a.nombre)?.estado ?? null;
                      const e = ESTADOS_APERTURA[estado ?? "sin_confirmar"];
                      return (
                        <section key={a.nombre} className={s.paso}>
                          <div className={s.cabecera}>
                            <div>
                              <Eyebrow tone="brand">{a.nombre === p.whatsapp.porDefecto ? "Por defecto" : "Apertura"}</Eyebrow>
                              <Text size="sm" weight="semibold" tone="strong">
                                {a.descripcion}
                              </Text>
                            </div>
                            <div style={{ display: "flex", gap: "var(--s-3)", alignItems: "center" }}>
                              <Badge tone={e.tono} size="sm" dot>
                                {e.etiqueta}
                              </Badge>
                              <Button size="sm" variant="outline" icon="copy" onClick={() => copiar(a.texto)}>
                                Copiar
                              </Button>
                            </div>
                          </div>
                          <KeyValueList tone="plain" layout="columns">
                            <KeyValue label="Nombre en Meta" mono>
                              {a.nombre}
                            </KeyValue>
                            <KeyValue label="Idioma" mono>
                              {a.idioma}
                            </KeyValue>
                          </KeyValueList>
                          <TextoMensaje segmentos={a.segmentos} />
                          <Text size="xs" tone="muted">
                            En violeta, lo que se completa por inmobiliaria: {"{{1}}"} quien envía y {"{{2}}"} la zona. Para cargarla en WhatsApp Manager: categoría marketing,
                            idioma español (Argentina), con Lautaro y Palermo de ejemplo.
                          </Text>
                        </section>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <TextoMensaje segmentos={[{ texto: p.whatsapp.textoLibre, variable: false }]} />
                  <Text size="xs" tone="muted">
                    {p.whatsapp.redaccion === "claude"
                      ? "La primera línea la redacta Claude para cada inmobiliaria; el resto es fijo."
                      : "La primera línea sale de una plantilla por zona. Con ANTHROPIC_API_KEY la redacta Claude."}
                  </Text>
                  <div className={s.sugerida}>
                    <div className={s.cabecera}>
                      <div>
                        <Heading level={3} size="sm">
                          Texto para cargar en WhatsApp Manager
                        </Heading>
                        <Text size="xs" tone="muted">
                          Con la API oficial hace falta una plantilla aprobada. Esta es la apertura por defecto del código; se activa con WHATSAPP_PLANTILLA.
                        </Text>
                      </div>
                      {p.whatsapp.aperturas[0] ? (
                        <Button size="sm" variant="outline" icon="copy" onClick={() => copiar(p.whatsapp.aperturas[0]!.texto)}>
                          Copiar
                        </Button>
                      ) : null}
                    </div>
                    {p.whatsapp.aperturas[0] ? <pre className={s.codigo}>{p.whatsapp.aperturas[0].texto}</pre> : null}
                  </div>
                </>
              )}
            </Card>

            <Card padding="lg">
              <div className={s.cabecera}>
                <div>
                  <Eyebrow>Email · {p.email.secuencia}</Eyebrow>
                  <Heading level={2} size="md">
                    Secuencia de tres correos
                  </Heading>
                </div>
                <Badge tone="neutral" size="md" dot>
                  En pausa: todavía no se manda email
                </Badge>
              </div>
              <div className={s.pasos}>
                {p.email.pasos.map((e) => (
                  <section key={e.paso} className={s.paso}>
                    <Eyebrow tone="brand">
                      Paso {e.paso}
                      {e.paso === 2 ? " · 4 días después" : e.paso === 3 ? " · 7 días después" : ""}
                    </Eyebrow>
                    <Text size="sm" weight="semibold" tone="strong">
                      {e.asunto}
                    </Text>
                    <pre className={s.correo}>{e.texto}</pre>
                  </section>
                ))}
              </div>
            </Card>
          </>
        )}
      </Page>
    </>
  );
}
